import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Form,
  FormGroup,
  Badge,
  UncontrolledTooltip,
  NavItem,
} from "reactstrap";
import { fetchData, postData } from "../../services/apiServices";
import { useNavigate } from "react-router-dom";
import WithdrawTable from "./WithdrawTable";

const Withdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({});
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [active, setActive] = useState("all");

  const navigate = useNavigate();

  const handleViewWithdraw = (id) => {
    navigate(`/withdraws/${id}`);
  };

  // Modal states
  const [singleWithdrawModal, setSingleWithdrawModal] = useState({
    show: false,
    withdrawal: null,
    loading: false,
    currency: "USDC",
    feePlan: "normal",
    processor: "plisio", // 'plisio' or 'mileston'
  });

  const [massWithdrawModal, setMassWithdrawModal] = useState({
    show: false,
    loading: false,
    currency: "USDC",
    feePlan: "normal",
    processor: "plisio", // 'plisio' or 'mileston'
  });

  const [processResult, setProcessResult] = useState({
    show: false,
    success: false,
    message: "",
    data: null,
  });

  // Fetch pending withdrawals
  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchData("api/wallet/withdraws");

      if (response && response.success) {
        setWithdrawals(response.withdraws || []);
        setSummary(response.summary || {});
      } else {
        // Parse error message from response
        let errorMessage = response?.message || "Failed to fetch withdrawals";

        // If there's an error field with JSON string, try to parse it
        if (response?.error) {
          try {
            const parsedError = JSON.parse(response.error);
            const errorDetails = Object.entries(parsedError)
              .map(([key, value]) => {
                if (Array.isArray(value)) {
                  return `${key}: ${value.join(", ")}`;
                }
                return `${key}: ${value}`;
              })
              .join("; ");
            errorMessage = `${errorMessage}. ${errorDetails}`;
          } catch (parseError) {
            // If parsing fails, use the raw error string
            errorMessage = `${errorMessage}. ${response.error}`;
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching withdrawals:", error);

      let errorMessage = error.message || "Failed to fetch withdrawals";

      // Check if the error has a response with our API error format
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (responseData.message) {
          errorMessage = responseData.message;

          // If there's an error field with JSON string, try to parse it
          if (responseData.error) {
            try {
              const parsedError = JSON.parse(responseData.error);
              const errorDetails = Object.entries(parsedError)
                .map(([key, value]) => {
                  if (Array.isArray(value)) {
                    return `${key}: ${value.join(", ")}`;
                  }
                  return `${key}: ${value}`;
                })
                .join("; ");
              errorMessage = `${errorMessage}. ${errorDetails}`;
            } catch (parseError) {
              // If parsing fails, use the raw error string
              errorMessage = `${errorMessage}. ${responseData.error}`;
            }
          }
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  //filter
  const AllWithdrawals = withdrawals.filter(
    (withdraw) => withdraw.status == "pending" || "rejected" || "rejected"
  );

  const pendingWithdrawals = withdrawals.filter(
    (withdraw) => withdraw.status == "pending"
  );

  const completedWithdrawals = withdrawals.filter(
    (withdraw) => withdraw.status == "completed"
  );
  const failedWithdrawals = withdrawals.filter(
    (withdraw) => withdraw.status == "rejected"
  );

  // Handle single withdrawal selection
  const handleSelectWithdrawal = (withdrawalId) => {
    setSelectedWithdrawals((prev) => {
      if (prev.includes(withdrawalId)) {
        return prev.filter((id) => id !== withdrawalId);
      } else {
        return [...prev, withdrawalId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedWithdrawals([]);
    } else {
      setSelectedWithdrawals(withdrawals.map((w) => w.id));
    }
    setSelectAll(!selectAll);
  };

  // Open single withdrawal modal
  const openSingleWithdrawModal = (withdrawal) => {
    setSingleWithdrawModal({
      show: true,
      withdrawal,
      loading: false,
      currency: "USDC",
      feePlan: "normal",
      processor: "plisio", // Default to Plisio
    });
  };

  // Open mass withdrawal modal
  const openMassWithdrawModal = () => {
    if (selectedWithdrawals.length === 0) {
      alert("Please select at least one withdrawal to process");
      return;
    }

    setMassWithdrawModal({
      show: true,
      loading: false,
      currency: "USDC",
      feePlan: "normal",
      processor: "plisio", // Default to Plisio
    });
  };

  // Process single withdrawal
  const processSingleWithdrawal = async () => {
    try {
      setSingleWithdrawModal((prev) => ({ ...prev, loading: true }));

      let response;

      if (singleWithdrawModal.processor === "mileston") {
        // Mileston API call
        const payload = {
          withdrawId: singleWithdrawModal.withdrawal.id,
        };
        response = await postData("payouts/mileston/single-payout", payload);
      } else {
        // Plisio API call
        const payload = {
          withdrawId: singleWithdrawModal.withdrawal.id,
          currency: singleWithdrawModal.currency,
          feePlan: singleWithdrawModal.feePlan,
        };
        response = await postData("payments/plisio/withdraw", payload);
      }

      if (response && response.success) {
        setProcessResult({
          show: true,
          success: true,
          message: response.message,
          data: response,
        });

        // Refresh withdrawals list
        await fetchWithdrawals();

        // Close modal
        setSingleWithdrawModal({
          show: false,
          withdrawal: null,
          loading: false,
          currency: "USDC",
          feePlan: "normal",
          processor: "plisio",
        });
      } else {
        // Parse error message from response
        let errorMessage = response?.message || "Failed to process withdrawal";

        // If there's an error field with JSON string, try to parse it
        if (response?.error) {
          try {
            const parsedError = JSON.parse(response.error);
            const errorDetails = Object.entries(parsedError)
              .map(([key, value]) => {
                if (Array.isArray(value)) {
                  return `${key}: ${value.join(", ")}`;
                }
                return `${key}: ${value}`;
              })
              .join("; ");
            errorMessage = `${errorMessage}. ${errorDetails}`;
          } catch (parseError) {
            // If parsing fails, use the raw error string
            errorMessage = `${errorMessage}. ${response.error}`;
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error processing withdrawal:", error);

      let errorMessage = error.message || "Failed to process withdrawal";

      // Check if the error has a response with our API error format
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (responseData.message) {
          errorMessage = responseData.message;

          // If there's an error field with JSON string, try to parse it
          if (responseData.error) {
            try {
              const parsedError = JSON.parse(responseData.error);
              const errorDetails = Object.entries(parsedError)
                .map(([key, value]) => {
                  if (Array.isArray(value)) {
                    return `${key}: ${value.join(", ")}`;
                  }
                  return `${key}: ${value}`;
                })
                .join("; ");
              errorMessage = `${errorMessage}. ${errorDetails}`;
            } catch (parseError) {
              // If parsing fails, use the raw error string
              errorMessage = `${errorMessage}. ${responseData.error}`;
            }
          }
        }
      }

      setProcessResult({
        show: true,
        success: false,
        message: errorMessage,
        data: null,
      });
    } finally {
      setSingleWithdrawModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Process mass withdrawal
  const processMassWithdrawal = async () => {
    try {
      setMassWithdrawModal((prev) => ({ ...prev, loading: true }));

      const payload = {
        withdrawIds: selectedWithdrawals,
        currency: massWithdrawModal.currency,
        feePlan: massWithdrawModal.feePlan,
      };

      const response = await postData("payments/plisio/mass-withdraw", payload);

      if (response && response.success) {
        setProcessResult({
          show: true,
          success: true,
          message: response.message,
          data: response,
        });

        // Refresh withdrawals list
        await fetchWithdrawals();

        // Clear selections and close modal
        setSelectedWithdrawals([]);
        setSelectAll(false);
        setMassWithdrawModal({
          show: false,
          loading: false,
          currency: "USDC",
          feePlan: "normal",
          processor: "plisio",
        });
      } else {
        // Parse error message from response
        let errorMessage =
          response?.message || "Failed to process mass withdrawal";

        // If there's an error field with JSON string, try to parse it
        if (response?.error) {
          try {
            const parsedError = JSON.parse(response.error);
            const errorDetails = Object.entries(parsedError)
              .map(([key, value]) => {
                if (Array.isArray(value)) {
                  return `${key}: ${value.join(", ")}`;
                }
                return `${key}: ${value}`;
              })
              .join("; ");
            errorMessage = `${errorMessage}. ${errorDetails}`;
          } catch (parseError) {
            // If parsing fails, use the raw error string
            errorMessage = `${errorMessage}. ${response.error}`;
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error processing mass withdrawal:", error);

      let errorMessage = error.message || "Failed to process mass withdrawal";

      // Check if the error has a response with our API error format
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (responseData.message) {
          errorMessage = responseData.message;

          // If there's an error field with JSON string, try to parse it
          if (responseData.error) {
            try {
              const parsedError = JSON.parse(responseData.error);
              const errorDetails = Object.entries(parsedError)
                .map(([key, value]) => {
                  if (Array.isArray(value)) {
                    return `${key}: ${value.join(", ")}`;
                  }
                  return `${key}: ${value}`;
                })
                .join("; ");
              errorMessage = `${errorMessage}. ${errorDetails}`;
            } catch (parseError) {
              // If parsing fails, use the raw error string
              errorMessage = `${errorMessage}. ${responseData.error}`;
            }
          }
        }
      }

      setProcessResult({
        show: true,
        success: false,
        message: errorMessage,
        data: null,
      });
    } finally {
      setMassWithdrawModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format amount
  const formatAmount = (amount) => {
    return parseFloat(amount).toFixed(8);
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
            <p className="mt-2">Loading pending withdrawals...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 font-size-18">Pending Withdrawals</h4>
            </div>
          </Col>
        </Row>

        {error && (
          <Row>
            <Col>
              <Alert color="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        {/* Summary Card */}
        <Row>
          <Col xl="12">
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Summary</h5>
                <Row>
                  <Col md="3">
                    <div className="text-center">
                      <h5 className="text-primary">
                        {summary.total_count || 0}
                      </h5>
                      <p className="text-muted mb-0">Total Withdrawals</p>
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="text-center">
                      <h5 className="text-success">
                        {formatAmount(summary.total_amount || 0)}
                      </h5>
                      <p className="text-muted mb-0">Total Amount</p>
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="text-center">
                      <h5 className="text-info">
                        {selectedWithdrawals.length}
                      </h5>
                      <p className="text-muted mb-0">Selected</p>
                    </div>
                  </Col>
                  <Col md="3">
                    <div className="text-center">
                      <Button
                        color="primary"
                        onClick={openMassWithdrawModal}
                        disabled={selectedWithdrawals.length === 0}>
                        Process Selected ({selectedWithdrawals.length})
                      </Button>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Withdrawals Table */}

        <Row className="d-flex flex-column">
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-start align-items-center gap-1 mb-3">
                  <Button
                    color={`${active == "all" ? "success" : "secondary"}`}
                    size="sm"
                    onClick={() => setActive("all")}>
                    All
                  </Button>
                  <Button
                    color={`${active == "completed" ? "success" : "secondary"}`}
                    size="sm"
                    onClick={() => setActive("completed")}>
                    Completed
                  </Button>
                  <Button
                    color={`${active == "pending" ? "success" : "secondary"}`}
                    size="sm"
                    onClick={() => setActive("pending")}>
                    Pending
                  </Button>
                  <Button
                    color={`${active == "rejected" ? "success" : "secondary"}`}
                    size="sm"
                    onClick={() => setActive("rejected")}>
                    rejected
                  </Button>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    {active == "all" && " All Withdrawals"}
                    {active == "completed" && " Completed Withdrawals"}
                    {active == "rejected" && " rejected Withdrawals"}
                    {active == "pending" && " Pending Withdrawals"}
                  </h5>

                  <div className="d-flex justify-content-end align-items-center gap-2">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={fetchWithdrawals}>
                      <i className="mdi mdi-refresh me-1"></i>
                      Refresh
                    </Button>
                  </div>
                </div>

                {withdrawals.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No pending withdrawals found</p>
                  </div>
                ) : (
                  <>
                    {active == "all" && (
                      <WithdrawTable
                        withdrawals={AllWithdrawals}
                        selectAll={selectAll}
                        handleSelectAll={handleSelectAll}
                        selectedWithdrawals={selectedWithdrawals}
                        handleSelectWithdrawal={handleSelectWithdrawal}
                        formatDate={formatDate}
                        formatAmount={formatAmount}
                      />
                    )}
                    {active == "completed" && (
                      <WithdrawTable
                        withdrawals={completedWithdrawals}
                        selectAll={selectAll}
                        handleSelectAll={handleSelectAll}
                        selectedWithdrawals={selectedWithdrawals}
                        handleSelectWithdrawal={handleSelectWithdrawal}
                        formatDate={formatDate}
                        formatAmount={formatAmount}
                      />
                    )}
                    {active == "pending" && (
                      <WithdrawTable
                        withdrawals={pendingWithdrawals}
                        selectAll={selectAll}
                        handleSelectAll={handleSelectAll}
                        selectedWithdrawals={selectedWithdrawals}
                        handleSelectWithdrawal={handleSelectWithdrawal}
                        formatDate={formatDate}
                        formatAmount={formatAmount}
                      />
                    )}
                    {active == "rejected" && (
                      <WithdrawTable
                        withdrawals={failedWithdrawals}
                        selectAll={selectAll}
                        handleSelectAll={handleSelectAll}
                        selectedWithdrawals={selectedWithdrawals}
                        handleSelectWithdrawal={handleSelectWithdrawal}
                        formatDate={formatDate}
                        formatAmount={formatAmount}
                      />
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Single Withdrawal Modal */}
        <Modal
          isOpen={singleWithdrawModal.show}
          toggle={() =>
            setSingleWithdrawModal((prev) => ({ ...prev, show: false }))
          }>
          <ModalHeader
            toggle={() =>
              setSingleWithdrawModal((prev) => ({ ...prev, show: false }))
            }>
            Process Single Withdrawal
          </ModalHeader>
          <ModalBody>
            {singleWithdrawModal.withdrawal && (
              <div>
                <p>
                  <strong>User:</strong>{" "}
                  {singleWithdrawModal.withdrawal.user.username} (
                  {singleWithdrawModal.withdrawal.user.email})
                </p>
                <p>
                  <strong>Amount:</strong>{" "}
                  {formatAmount(singleWithdrawModal.withdrawal.amount)}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  <code>{singleWithdrawModal.withdrawal.address}</code>
                </p>

                <Form>
                  <FormGroup>
                    <Label for="processor">Payment Processor</Label>
                    <Input
                      type="select"
                      id="processor"
                      value={singleWithdrawModal.processor}
                      onChange={(e) =>
                        setSingleWithdrawModal((prev) => ({
                          ...prev,
                          processor: e.target.value,
                        }))
                      }>
                      <option value="plisio">Plisio</option>
                      <option value="mileston">Mileston</option>
                    </Input>
                    <small className="text-muted">
                      {singleWithdrawModal.processor === "plisio"
                        ? "Process through Plisio payment gateway with customizable fee plans"
                        : "Process through Mileston with fixed settings"}
                    </small>
                  </FormGroup>

                  {singleWithdrawModal.processor === "plisio" && (
                    <>
                      <FormGroup>
                        <Label for="currency">Currency</Label>
                        <Input
                          type="select"
                          id="currency"
                          value={singleWithdrawModal.currency}
                          onChange={(e) =>
                            setSingleWithdrawModal((prev) => ({
                              ...prev,
                              currency: e.target.value,
                            }))
                          }>
                          <option value="USDC">USD Coin (USDC)</option>
                          <option value="USDT">Tether (USDT)</option>
                        </Input>
                      </FormGroup>

                      <FormGroup>
                        <Label for="feePlan">Fee Plan</Label>
                        <Input
                          type="select"
                          id="feePlan"
                          value={singleWithdrawModal.feePlan}
                          onChange={(e) =>
                            setSingleWithdrawModal((prev) => ({
                              ...prev,
                              feePlan: e.target.value,
                            }))
                          }>
                          <option value="normal">Normal</option>
                          <option value="priority">Priority</option>
                          <option value="economy">Economy</option>
                        </Input>
                      </FormGroup>
                    </>
                  )}
                </Form>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() =>
                setSingleWithdrawModal((prev) => ({ ...prev, show: false }))
              }>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={processSingleWithdrawal}
              disabled={singleWithdrawModal.loading}>
              {singleWithdrawModal.loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                "Process Withdrawal"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Mass Withdrawal Modal */}
        <Modal
          isOpen={massWithdrawModal.show}
          toggle={() =>
            setMassWithdrawModal((prev) => ({ ...prev, show: false }))
          }>
          <ModalHeader
            toggle={() =>
              setMassWithdrawModal((prev) => ({ ...prev, show: false }))
            }>
            Process Mass Withdrawal
          </ModalHeader>
          <ModalBody>
            <p>
              <strong>Selected Withdrawals:</strong>{" "}
              {selectedWithdrawals.length}
            </p>
            <p className="text-muted">
              This will process all selected withdrawals in a single batch
              transaction.
            </p>

            <Form>
              <FormGroup>
                <Label for="massProcessor">Payment Processor</Label>
                <Input
                  type="select"
                  id="massProcessor"
                  value={massWithdrawModal.processor}
                  onChange={(e) =>
                    setMassWithdrawModal((prev) => ({
                      ...prev,
                      processor: e.target.value,
                    }))
                  }>
                  <option value="plisio">Plisio (Batch Processing)</option>
                </Input>
                <small className="text-muted">
                  Mass withdrawals are currently only supported through Plisio
                </small>
              </FormGroup>

              <FormGroup>
                <Label for="massCurrency">Currency</Label>
                <Input
                  type="select"
                  id="massCurrency"
                  value={massWithdrawModal.currency}
                  onChange={(e) =>
                    setMassWithdrawModal((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }>
                  <option value="USDC">USD Coin (USDC)</option>
                  <option value="USDT">Tether (USDT)</option>
                </Input>
              </FormGroup>

              <FormGroup>
                <Label for="massFeePlan">Fee Plan</Label>
                <Input
                  type="select"
                  id="massFeePlan"
                  value={massWithdrawModal.feePlan}
                  onChange={(e) =>
                    setMassWithdrawModal((prev) => ({
                      ...prev,
                      feePlan: e.target.value,
                    }))
                  }>
                  <option value="normal">Normal</option>
                  <option value="priority">Priority</option>
                  <option value="economy">Economy</option>
                </Input>
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() =>
                setMassWithdrawModal((prev) => ({ ...prev, show: false }))
              }>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={processMassWithdrawal}
              disabled={massWithdrawModal.loading}>
              {massWithdrawModal.loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                `Process ${selectedWithdrawals.length} Withdrawals`
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Result Modal */}
        <Modal
          isOpen={processResult.show}
          toggle={() => setProcessResult((prev) => ({ ...prev, show: false }))}>
          <ModalHeader
            toggle={() =>
              setProcessResult((prev) => ({ ...prev, show: false }))
            }>
            {processResult.success ? "Success" : "Error"}
          </ModalHeader>
          <ModalBody>
            <Alert color={processResult.success ? "success" : "danger"}>
              {processResult.message}
            </Alert>

            {processResult.success && processResult.data && (
              <div>
                {/* Plisio Response */}
                {processResult.data.withdrawal && (
                  <div>
                    <h6>Transaction Details:</h6>
                    <p>
                      <strong>Status:</strong>{" "}
                      {processResult.data.withdrawal.status}
                    </p>
                    {processResult.data.withdrawal.tx_url && (
                      <p>
                        <strong>Transaction:</strong>{" "}
                        <a
                          href={processResult.data.withdrawal.tx_url}
                          target="_blank"
                          rel="noopener noreferrer">
                          View on Blockchain
                        </a>
                      </p>
                    )}
                    {processResult.data.withdrawal.total_amount && (
                      <p>
                        <strong>Total Amount:</strong>{" "}
                        {processResult.data.withdrawal.total_amount}{" "}
                        {processResult.data.withdrawal.currency}
                      </p>
                    )}
                    {processResult.data.withdrawal.total_fee && (
                      <p>
                        <strong>Total Fee:</strong>{" "}
                        {processResult.data.withdrawal.total_fee}{" "}
                        {processResult.data.withdrawal.currency}
                      </p>
                    )}
                  </div>
                )}

                {/* Mileston Response */}
                {processResult.data.payout && (
                  <div>
                    <h6>Payout Details:</h6>
                    <p>
                      <strong>Status:</strong>{" "}
                      {processResult.data.payout.statusCode === 200
                        ? "Success"
                        : "Failed"}
                    </p>
                    <p>
                      <strong>Amount:</strong>{" "}
                      {processResult.data.payout.amount}
                    </p>
                    <p>
                      <strong>Recipient:</strong>{" "}
                      <code>{processResult.data.payout.recipientAddress}</code>
                    </p>
                    <p>
                      <strong>Wallet Type:</strong>{" "}
                      {processResult.data.payout.walletType}
                    </p>
                    {processResult.data.payout.message && (
                      <p>
                        <strong>Message:</strong>{" "}
                        {processResult.data.payout.message}
                      </p>
                    )}
                  </div>
                )}

                {/* Withdrawal Request Details */}
                {processResult.data.withdraw_request && (
                  <div className="mt-3">
                    <h6>Withdrawal Request:</h6>
                    <p>
                      <strong>User:</strong>{" "}
                      {processResult.data.withdraw_request.user}
                    </p>
                    <p>
                      <strong>Amount:</strong>{" "}
                      {processResult.data.withdraw_request.amount}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <Badge color="success">
                        {processResult.data.withdraw_request.status}
                      </Badge>
                    </p>
                  </div>
                )}

                {processResult.data.processed_withdrawals && (
                  <div className="mt-3">
                    <h6>Processed Withdrawals:</h6>
                    <ul>
                      {processResult.data.processed_withdrawals.map(
                        (pw, index) => (
                          <li key={index}>
                            {pw.user}: {pw.amount}{" "}
                            {processResult.data.withdrawal.currency} -{" "}
                            <Badge color="success">{pw.status}</Badge>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!processResult.success &&
              processResult.data &&
              processResult.data.failed_withdrawals && (
                <div className="mt-3">
                  <h6>Failed Withdrawals:</h6>
                  <ul>
                    {processResult.data.failed_withdrawals.map((fw, index) => (
                      <li key={index} className="text-danger">
                        {fw.user}: {fw.amount} -{" "}
                        <Badge color="danger">{fw.status}</Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() =>
                setProcessResult((prev) => ({ ...prev, show: false }))
              }>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Withdrawals;
